import { toast } from 'react-toastify';
import axios from 'axios';
import { generateSurfaceUrl, PREVIEW } from './environments';
import { updateUserIpInCookie } from './api_utils';
import { builderSurfaceJsonContentHeader } from './restApiHeaderUtils';
import showAlert from './alert';
import { loadOrganizationDetails } from './check-auth';
import cookie from 'react-cookies';
import { checkGithubPermission } from './permissions';
import { STRING_STYLE, stringDecor } from './string-util';
import { BUILD_SUCCESSFUL, FAILED_TO_PUBLISH, NETWORK_ERROR, TRY_AGAIN } from './constant';

const { REACT_APP_BUILDER_ENGINE, REACT_APP_ENVIRONMENT } = process.env;

export const publishAppOnServer = async (
  envType,
  name,
  updateState,
  selVersion,
  projectDetail,
  actions,
  cleanPublish,
) => {
  const preConfirm = async () => {
    updateState(true);
    const { seoName, uuid } = projectDetail;
    let environment = REACT_APP_ENVIRONMENT || '';
    if (environment) {
      environment = stringDecor(environment, { trim: true, style: STRING_STYLE.L });
    }
    let protocol = 'https';
    if (environment.startsWith('dev')) {
      protocol = `http`;
    }
    let exchangeEngineUrl = generateSurfaceUrl(envType, seoName);
    const publishURL = `${exchangeEngineUrl}api/v1/projects/build/${uuid}/version/${selVersion}`;
    try {
      toast.info('Started Publishing the Application.');
      await actions.updateLatestPublishVersion({
        projectId: uuid,
        latestVersion: selVersion,
        envType,
      });
      await updatePublishSaveData(uuid, selVersion, envType);
      const res = await axios.post(
        publishURL,
        {
          subscription: loadOrganizationDetails('subscription'),
          cleanPublish: cleanPublish,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            projectId: uuid,
          },
        },
      );

      updateState(false);
      if (res.data.message === 'Build Success') {
        toast.success(BUILD_SUCCESSFUL);
        // await updatePublishSaveData(uuid, selVersion, envType);
        await pushCodeToGitHub(protocol, seoName, exchangeEngineUrl, uuid, envType);
        return { success: true, data: res.data };
      } else {
        toast.error(FAILED_TO_PUBLISH, {});
        return { success: false, error: FAILED_TO_PUBLISH };
      }
    } catch (error) {
      updateState(false);
      if (stringDecor(error.message, { strict: true, style: STRING_STYLE.L }) === NETWORK_ERROR) {
        toast.error(TRY_AGAIN, {});
      } else {
        toast.error(`Failed: ${error.message}`, {});
        return { success: false, error: error.message };
      }
    }
  };

  if (envType === PREVIEW) {
    return await preConfirm();
  } else {
    const title = `You are going to publish the project in ${name}:(${stringDecor(envType, {
      style: STRING_STYLE.T,
    })}) environment. Are you sure?`;
    showAlert(title, preConfirm, 'Publish');
  }
};

export const publishVibeCodeOnServer = async ({
  envType,
  name,
  updateState,
  selVersion,
  projectDetail,
  actions,
}) => {
  const preConfirm = async () => {
    updateState(true);
    let environment = process.env.REACT_APP_ENVIRONMENT || '';
    if (environment) {
      environment = stringDecor(environment, { trim: true, style: STRING_STYLE.L });
    }
    let protocol = 'https';
    //Overriding publish Exchange Engine API URL protocol for DEV environment
    if (environment.startsWith('dev')) {
      protocol = `http`;
    }

    try {
      toast.info('Started Publishing the Application.');
      const res = await actions.publishVibeCode({
        projectId: projectDetail.uuid,
        latestVersion: selVersion,
        envType,
        subscription: loadOrganizationDetails('subscription'),
        cleanPublish: false,
      });

      updateState(false);
      if (res.success) {
        toast.success(BUILD_SUCCESSFUL);
      } else {
        toast.error(FAILED_TO_PUBLISH, {});
      }
    } catch (error) {
      updateState(false);
      if (stringDecor(error.message, { strict: true, style: STRING_STYLE.L }) === NETWORK_ERROR) {
        toast.error(TRY_AGAIN, {});
      } else {
        toast.error(`Failed: ${error.message}`, {});
      }
    }
  };

  if (envType === PREVIEW) {
    return await preConfirm();
  } else {
    const title = `You are going to publish the project in ${name}:(${stringDecor(envType, {
      style: STRING_STYLE.T,
    })}) environment. Are you sure?`;
    showAlert(title, preConfirm, 'Publish');
  }
};

export const updatePublishSaveData = async (uuid, version, envType) => {
  try {
    const userIp = cookie.load('userIp') || (await updateUserIpInCookie());
    let header = builderSurfaceJsonContentHeader(uuid, userIp);
    header.headers['x-project-env'] = envType;
    await axios.post(
      `${REACT_APP_BUILDER_ENGINE}publish/savedata`,
      { projectId: uuid, version },
      header,
    );
  } catch (error) {
    console.log('error in tracking publish', error);
  }
};

export const pushCodeToGitHub = async (protocol, seoName, exchangeEngineUrl, uuid, envType) => {
  const isAllowed = checkGithubPermission();
  console.log('This is the github Permission: ', isAllowed);
  if (!isAllowed) {
    console.log('This feature is not enabled for you. Please upgrade your account or contact us.');
    return {
      message: 'This feature is not enabled for you. Please upgrade your account or contact us.',
    };
  }
  const existingCookie = cookie.load(`github_publish_${envType}`);
  if (existingCookie) {
    console.log('GitHub push skipped — already pushed recently');
    return { message: 'Push skipped — wait 5 minutes before retrying' };
  }

  const githubURL = `${protocol}://${seoName}.${exchangeEngineUrl}api/v1/`;
  try {
    const { data } = await axios.post(
      `${githubURL}github/push-to-github`,
      { envType },
      {
        headers: {
          'Content-Type': 'application/json',
          'x-project-id': uuid,
        },
      },
    );

    const { success } = data;
    if (success) {
      console.log('Code successfully pushed to github');
      const expires = new Date();
      expires.setTime(expires.getTime() + 5 * 60 * 1000);
      cookie.save(`github_publish_${envType}`, 'true', { path: '/', expires });
    } else {
      console.log('Failed to push to github');
    }
  } catch (error) {
    if (error.response && error.response.data) {
      console.log('Failed to push to github:', error);
    } else {
      console.log('Error while pushing the code to github');
    }
    console.error('Error while pushing the code to GitHub:', error);
  }
};
